import React from 'react';
import { LockIcon, ShieldCheckIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { useGovernance } from '../../contexts/GovernanceContext';
import type { Permission, Scope } from '../../types/governance';

/**
 * Renders children only when the signed-in administrator genuinely holds the
 * permission in the current scope. On refusal it states WHY — missing
 * permission, wrong scope, expired access or awaiting approval — because a
 * generic "not allowed" leaves an operator with nothing to act on.
 *
 * This is presentation only. The same evaluation is authoritative server-side.
 */
export function PermissionGate({
  permission,
  scope,
  children,
  label
}: {
  permission: Permission;
  scope?: Scope;
  children: React.ReactNode;
  /** What was being attempted, named in the refusal. */
  label: string;
}) {
  const {
    allows
  } = useGovernance();
  const decision = allows(permission, scope);
  if (decision.allowed) return <>{children}</>;
  return <div className="rounded-card border border-line bg-canvas p-4" data-id="element-425" data-mp="z35z5vji6bbe">
      <div className="flex flex-wrap items-center gap-2" data-id="element-426" data-mp="z3z3kpuzooqd">
        <Badge tone="neutral" icon={<LockIcon className="h-4 w-4" data-id="element-428" data-mp="1349duepte1b1" />} data-id="element-427" data-mp="1350ywvlscgn2">
          Not available to you
        </Badge>
        <span className="cz-num text-caption text-ink-subtle" data-id="element-429" data-mp="pcl0y97qr8t0">{permission}</span>
      </div>
      <p className="mt-2 max-w-[70ch] text-body-sm text-ink" data-id="element-430" data-mp="ub61yauthwii">
        {label} — {decision.message}
      </p>
    </div>;
}

/** A small marker showing which role granted an action, for provenance. */
export function GrantedVia({
  permission,
  scope
}: {
  permission: Permission;
  scope?: Scope;
}) {
  const {
    allows
  } = useGovernance();
  const decision = allows(permission, scope);
  if (!decision.allowed || !decision.viaRole) return null;
  return <Badge plain icon={<ShieldCheckIcon className="h-4 w-4" data-id="element-432" data-mp="20sswhqor9jv6" />} data-id="element-431" data-mp="bffvaabxwbya">
      via {decision.viaRole}
    </Badge>;
}